import type { AuthConfigDto, AuthenticatedUserDto } from '@tacobots-forms/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useMemo, type ReactNode } from 'react';
import { ApiError } from '@/shared/api/client';
import { authKeys, fetchAuthConfig, fetchCurrentUser, logout as logoutRequest } from './api';

interface AuthContextValue {
  user: AuthenticatedUserDto | null;
  config: AuthConfigDto | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Holds the current session.
 *
 * The browser never inspects a token: the session cookie is httpOnly, so "who
 * am I" is a question only the API can answer. A 401 simply means "nobody",
 * which is why it is not treated as an error.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: authKeys.config,
    queryFn: fetchAuthConfig,
    staleTime: 5 * 60_000,
  });

  const userQuery = useQuery({
    queryKey: authKeys.me,
    queryFn: async () => {
      try {
        return await fetchCurrentUser();
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: authKeys.me });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    const result = await logoutRequest();
    // Everything cached was fetched as this user; none of it survives sign-out.
    queryClient.clear();
    queryClient.setQueryData(authKeys.me, null);

    // With portal single sign-on, clearing only our cookie would leave the
    // shared one in place and the next request would sign the person back in.
    if (result?.redirectTo) window.location.assign(result.redirectTo);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: userQuery.data ?? null,
      config: configQuery.data ?? null,
      isLoading: userQuery.isLoading || configQuery.isLoading,
      refresh,
      signOut,
    }),
    [
      userQuery.data,
      userQuery.isLoading,
      configQuery.data,
      configQuery.isLoading,
      refresh,
      signOut,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
